import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useModalZIndex } from '../contexts/ModalStackContext';
import { X, Mail, Phone, Clock, ChevronDown, Plus } from 'lucide-react';
import { format } from 'date-fns';
import { supabase } from '../lib/supabase';
import { triggerGcalSync } from '../lib/bookings';
import { useBusiness } from '../contexts/BusinessContext';
import toast from 'react-hot-toast';
import SchedulePickerPanel from './booking/SchedulePickerPanel';
import { getProjectServices, ProjectService, addProjectService } from '../lib/projectServices';
import { formatCurrency } from '../lib/utils';
import { getCustomer, Customer } from '../lib/customers';
import { getPersonInitials } from '../utils/personInitials';
import SelectCustomerModal from './manage/SelectCustomerModal';
import EditCustomerModal from './manage/EditCustomerModal';
import SelectServiceModal from './manage/SelectServiceModal';
import SelectResourceModal from './manage/SelectResourceModal';
import AddressAutocompleteModal from './AddressAutocompleteModal';
import ColorPickerModal from './ColorPickerModal';
import CreateColorPresetModal from './CreateColorPresetModal';
import { getColorPresets, createColorPreset, deleteColorPreset, ColorPreset } from '../lib/color-presets';
import { findOpenLinkableOpportunityForCustomer } from '../lib/crm/opportunityLinking';
import { recordOpportunityActivity } from '../lib/opportunityActivities';

interface ScheduleFieldQuoteModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** When null, schedules a standalone field quote (no project). */
  projectId: string | null;
  projectTitle: string | null;
  onSuccess?: (createdFieldQuoteId?: string) => void;
  mode?: 'create' | 'edit';
  isMeasurement?: boolean;
  /** CRM: link created field quote to this opportunity. */
  initialOpportunityId?: number | null;
  /** CRM: when set, proposal is linked after create (status booked, field_quote_id, payments). */
  initialProposal?: { id: string } | null;
  /** CRM / FAB: pre-selected customer when not loading from a project. */
  initialCustomer?: Customer | null;
  /** Calendar: pre-fill the schedule from a clicked/highlighted slot (create mode). */
  initialSchedule?: {
    startDate?: string;
    startTime?: string;
    endDate?: string;
    endTime?: string;
  } | null;
  fieldQuoteToEdit?: {
    id: string;
    start_date: string | null;
    start_time: string | null;
    end_date: string | null;
    end_time: string | null;
    field_quote_address?: string | null;
    resource?: {
      id: string;
      first_name: string;
      last_name: string;
      avatar_url?: string | null;
    } | null;
  } | null;
}

export default function ScheduleFieldQuoteModal({
  isOpen,
  onClose,
  projectId,
  projectTitle,
  onSuccess,
  mode = 'create',
  isMeasurement = false,
  initialOpportunityId = null,
  initialProposal = null,
  initialCustomer = null,
  initialSchedule = null,
  fieldQuoteToEdit
}: ScheduleFieldQuoteModalProps) {
  const { selectedBusiness } = useBusiness();
  const isStandalone = !projectId;
  const [isAnimating, setIsAnimating] = useState(false);
  // Auto-elevate above a parent modal (e.g. the Messages CustomerMessageModal's
  // create menu); standalone keeps the original z-index.
  const resolvedZIndex = useModalZIndex(100150);
  const [loading, setLoading] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endDate, setEndDate] = useState('');
  const [endTime, setEndTime] = useState('');
  const [projectServices, setProjectServices] = useState<ProjectService[]>([]);
  const [loadingServices, setLoadingServices] = useState(false);
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [loadingCustomer, setLoadingCustomer] = useState(false);
  const [isSelectCustomerModalOpen, setIsSelectCustomerModalOpen] = useState(false);
  const [isSelectServiceModalOpen, setIsSelectServiceModalOpen] = useState(false);
  const [isSelectResourceModalOpen, setIsSelectResourceModalOpen] = useState(false);
  const [selectedResources, setSelectedResources] = useState<any[]>([]);
  const [isResourcesExpanded, setIsResourcesExpanded] = useState(false);
  const [fieldQuoteAddress, setFieldQuoteAddress] = useState<string>('');
  const [isEditCustomerModalOpen, setIsEditCustomerModalOpen] = useState(false);
  const [schedulePickerOpen, setSchedulePickerOpen] = useState(false);

  // Colour picker — mirrors the Create Booking modal. Defaults to the field
  // quote / measurement status colour so appearance is unchanged until edited.
  const defaultQuoteColor = isMeasurement ? '#8b5cf6' : '#f97316';
  const [fieldQuoteColor, setFieldQuoteColor] = useState(defaultQuoteColor);
  const [fieldQuoteSecondaryColor, setFieldQuoteSecondaryColor] = useState<string | null>(null);
  const [isColorPickerModalOpen, setIsColorPickerModalOpen] = useState(false);
  const [isCreateColorPresetModalOpen, setIsCreateColorPresetModalOpen] = useState(false);
  const [colorPresets, setColorPresets] = useState<ColorPreset[]>([]);
  const [selectedColorMethod, setSelectedColorMethod] = useState<'custom' | string>('custom');

  const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      document.body.style.overflow = 'hidden';
      loadColorPresets();

      // Fetch project data when modal opens, or use CRM-provided customer
      if (projectId) {
        loadProjectServices();
        loadProjectCustomer();
      } else {
        setProjectServices([]);
        if (!fieldQuoteToEdit) {
          if (initialCustomer) {
            setCustomer(initialCustomer);
            // Do not clear address after hydrating customer — empty string was wiping CRM/FAB pre-seed.
            setFieldQuoteAddress(initialCustomer.address?.trim() || '');
          } else {
            setCustomer(null);
            setFieldQuoteAddress('');
          }
        }
      }

      // If editing an existing field quote, prefill schedule fields/address/resources
      if (fieldQuoteToEdit) {
        setStartDate(fieldQuoteToEdit.start_date || '');
        setStartTime(fieldQuoteToEdit.start_time || '');
        setEndDate(fieldQuoteToEdit.end_date || '');
        setEndTime(fieldQuoteToEdit.end_time || '');
        setFieldQuoteAddress(fieldQuoteToEdit.field_quote_address || '');

        // If a resource is attached to this field quote, pre-select it
        if (fieldQuoteToEdit.resource) {
          setSelectedResources([fieldQuoteToEdit.resource]);
          setIsResourcesExpanded(true);
        }
      } else {
        // Calendar slot clicks pre-seed the schedule; FAB/CRM start blank.
        setStartDate(initialSchedule?.startDate || '');
        setStartTime(initialSchedule?.startTime || '');
        setEndDate(initialSchedule?.endDate || '');
        setEndTime(initialSchedule?.endTime || '');
        // Reset colour to the natural status colour for new field quotes
        setFieldQuoteColor(defaultQuoteColor);
        setFieldQuoteSecondaryColor(null);
        setSelectedColorMethod('custom');
      }
    } else {
      document.body.style.overflow = 'unset';
      // Reset data when modal closes
      setProjectServices([]);
      setCustomer(null);
      setFieldQuoteAddress('');
      setStartDate('');
      setStartTime('');
      setEndDate('');
      setEndTime('');
      setSelectedResources([]);
      setIsResourcesExpanded(false);
      setSchedulePickerOpen(false);
      setFieldQuoteColor(defaultQuoteColor);
      setFieldQuoteSecondaryColor(null);
      setSelectedColorMethod('custom');
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, projectId, fieldQuoteToEdit, initialCustomer]);

  // Standalone field quote edit: load customer + address (no project to hydrate from).
  useEffect(() => {
    if (!isOpen || mode !== 'edit' || !fieldQuoteToEdit?.id || !supabase || projectId) return;

    let cancelled = false;
    (async () => {
      try {
        const { data, error } = await supabase
          .from('project_field_quotes')
          .select('customer_id, field_quote_address')
          .eq('id', fieldQuoteToEdit.id)
          .single();

        if (cancelled || error || !data) return;

        if (data.field_quote_address) {
          setFieldQuoteAddress(data.field_quote_address);
        }
        if (data.customer_id) {
          const c = await getCustomer(data.customer_id);
          if (!cancelled && c) setCustomer(c);
        }
      } catch (e) {
        console.error('Error loading standalone field quote for edit:', e);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isOpen, mode, fieldQuoteToEdit?.id, projectId]);

  const loadProjectServices = async () => {
    if (!projectId) return;
    
    try {
      setLoadingServices(true);
      const services = await getProjectServices(projectId);
      setProjectServices(services || []);
    } catch (err) {
      console.error('Error loading project services:', err);
      setProjectServices([]);
    } finally {
      setLoadingServices(false);
    }
  };

  const loadProjectCustomer = async () => {
    if (!projectId || !supabase) return;
    
    try {
      setLoadingCustomer(true);
      // First, fetch the project to get customer_id
      const { data: projectData, error: projectError } = await supabase
        .from('projects')
        .select('customer_id')
        .eq('id', projectId)
        .single();

      if (projectError) throw projectError;

      if (projectData?.customer_id) {
        const customerData = await getCustomer(projectData.customer_id);
        setCustomer(customerData);
        // Initialize field quote address from customer address if available
        if (!fieldQuoteAddress && (customerData as any).address) {
          setFieldQuoteAddress((customerData as any).address || '');
        }
      } else {
        setCustomer(null);
        setFieldQuoteAddress('');
      }
    } catch (err) {
      console.error('Error loading project customer:', err);
      setCustomer(null);
    } finally {
      setLoadingCustomer(false);
    }
  };

  const loadColorPresets = async () => {
    if (!selectedBusiness?.id) return;
    try {
      const presets = await getColorPresets(selectedBusiness.id);
      setColorPresets(presets);
    } catch (error) {
      console.error('Error loading color presets:', error);
    }
  };

  // Prefill the saved colour when editing an existing field quote.
  useEffect(() => {
    if (!isOpen || mode !== 'edit' || !fieldQuoteToEdit?.id || !supabase) return;

    let cancelled = false;
    (async () => {
      try {
        const { data, error } = await supabase
          .from('project_field_quotes')
          .select('color, secondary_color')
          .eq('id', fieldQuoteToEdit.id)
          .single();

        if (cancelled || error || !data) return;
        if (data.color) {
          setFieldQuoteColor(data.color);
          setSelectedColorMethod('custom');
        }
        setFieldQuoteSecondaryColor(data.secondary_color ?? null);
      } catch (e) {
        console.error('Error loading field quote colour:', e);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isOpen, mode, fieldQuoteToEdit?.id]);

  const handleTransitionEnd = () => {
    if (!isOpen) {
      setIsAnimating(false);
    }
  };

  const handleCustomerSelect = async (selectedCustomer: Customer) => {
    if (isStandalone) {
      setCustomer(selectedCustomer);
      if ((selectedCustomer as any)?.address) {
        setFieldQuoteAddress((selectedCustomer as any).address || '');
      }
      setIsSelectCustomerModalOpen(false);
      return;
    }

    if (!projectId || !supabase) return;

    try {
      const { error } = await supabase
        .from('projects')
        .update({ customer_id: selectedCustomer.id })
        .eq('id', projectId);

      if (error) throw error;

      setCustomer(selectedCustomer);
      // Default the field quote address to the customer's saved address
      // so that when you pick a customer this field is auto-populated,
      // but can still be edited independently afterwards.
      if ((selectedCustomer as any)?.address) {
        setFieldQuoteAddress((selectedCustomer as any).address || '');
      }
      setIsSelectCustomerModalOpen(false);
      toast.success('Customer added to project');
    } catch (err) {
      console.error('Error updating project customer:', err);
      toast.error('Failed to add customer to project');
    }
  };

  const handleServiceAdd = async (service: any, priceOption: any) => {
    if (!projectId || !selectedBusiness?.id) return;

    try {
      // Calculate tax and total
      const taxRate = service.tax_rate || 5;
      const taxExempt = service.tax_exempt || false;
      const taxAmount = taxExempt ? 0 : Math.round((priceOption.price * taxRate / 100) * 100) / 100;
      const totalAmount = Math.round((priceOption.price + taxAmount) * 100) / 100;

      await addProjectService({
        project_id: projectId,
        service_id: service.id,
        vehicle_id: null, // Projects can have services without vehicles
        name: service.name,
        description: service.description || null,
        price: priceOption.price,
        tax_exempt: taxExempt,
        tax_rate: taxRate,
        tax_amount: taxAmount,
        discount_amount: 0,
        total_amount: totalAmount,
        duration_minutes: priceOption.duration_minutes,
        price_option_id: priceOption.id || null,
        notes: null
      });

      // Reload services
      await loadProjectServices();
      setIsSelectServiceModalOpen(false);
      toast.success('Service added to project');
      
      return Promise.resolve();
    } catch (err) {
      console.error('Error adding service to project:', err);
      toast.error('Failed to add service to project');
      return Promise.resolve(); // Still resolve to close modal
    }
  };

  const handleResourceAdd = (resource: any) => {
    // Check if resource is already selected
    if (!selectedResources.some(r => r.id === resource.id)) {
      setSelectedResources(prev => [...prev, resource]);
    }
    setIsSelectResourceModalOpen(false);
  };

  const handleResourceRemove = (resourceId: string) => {
    setSelectedResources(prev => prev.filter(r => r.id !== resourceId));
  };

  const servicesComplete = isStandalone || projectServices.length > 0;

  const isFormComplete =
    !!selectedBusiness?.id &&
    !!customer &&
    servicesComplete &&
    (mode === 'create' ? selectedResources.length > 0 : true) &&
    !!fieldQuoteAddress &&
    !!startDate &&
    !!startTime &&
    !!endDate &&
    !!endTime;

  const scheduleServiceMinutes = isStandalone
    ? 60
    : Math.max(60, projectServices.reduce((total, service) => total + service.duration_minutes, 0));

  const handleSave = async () => {
    if (!selectedBusiness?.id || !supabase) {
      toast.error('Missing required information');
      return;
    }

    if (!isFormComplete) {
      toast.error('Please fill out all required fields before scheduling');
      return;
    }

    try {
      setLoading(true);

      if (mode === 'edit' && fieldQuoteToEdit) {
        // If this field quote isn't linked to a CRM opportunity yet, auto-link the
        // most recent open opportunity for the customer (same rule as proposals).
        const updatePayload: Record<string, any> = {
          start_date: startDate || null,
          start_time: startTime || null,
          end_date: endDate || null,
          end_time: endTime || null,
          color: fieldQuoteColor || null,
          secondary_color: fieldQuoteSecondaryColor || null
        };

        if (!isMeasurement && customer?.id) {
          try {
            const { data: existing } = await supabase
              .from('project_field_quotes')
              .select('pipeline_opportunities_id')
              .eq('id', fieldQuoteToEdit.id)
              .maybeSingle();

            if (existing && existing.pipeline_opportunities_id == null) {
              const linkable = await findOpenLinkableOpportunityForCustomer(
                selectedBusiness.id,
                customer.id
              );
              if (linkable) {
                updatePayload.pipeline_opportunities_id = linkable.id;
                toast.success(`Linked to CRM opportunity "${linkable.title}"`);
              }
            }
          } catch (linkErr) {
            console.error('Error auto-linking CRM opportunity for field quote:', linkErr);
          }
        }

        // Update existing field quote schedule (and opportunity link if resolved)
        const { error } = await supabase
          .from('project_field_quotes')
          .update(updatePayload)
          .eq('id', fieldQuoteToEdit.id);

        if (error) throw error;

        // If we just linked this field quote to an opportunity, record the action
        // so the opportunity is marked read.
        if (updatePayload.pipeline_opportunities_id && customer?.id) {
          recordOpportunityActivity({
            businessId: selectedBusiness.id,
            customerId: customer.id,
            activityType: 'field_quote_linked',
            activityData: { field_quote_id: fieldQuoteToEdit.id },
            specificOpportunityId: updatePayload.pipeline_opportunities_id,
          }).catch(err => console.error('Error recording field_quote_linked activity:', err));
        }

        if (selectedBusiness?.id) {
          triggerGcalSync(fieldQuoteToEdit.id, 'update', selectedBusiness.id, 'field_quote');
        }

        toast.success('Field quote updated successfully');
      } else {
        // Resolve the opportunity to link. If one wasn't passed in (e.g. a direct
        // field quote, no CRM lead opened), auto-link the most recent open
        // opportunity for this customer — exactly like proposals do. Measurements
        // are never treated as a sales origin, so they don't auto-link.
        let opportunityId: number | null = initialOpportunityId ?? null;
        if (opportunityId == null && !isMeasurement && customer?.id) {
          try {
            const linkable = await findOpenLinkableOpportunityForCustomer(
              selectedBusiness.id,
              customer.id
            );
            if (linkable) {
              opportunityId = linkable.id;
              toast.success(`Linked to CRM opportunity "${linkable.title}"`);
            }
          } catch (linkErr) {
            console.error('Error auto-linking CRM opportunity for field quote:', linkErr);
          }
        }

        // Create the field quote
        const { data: fieldQuote, error } = await supabase
          .from('project_field_quotes')
          .insert({
            project_id: projectId || null,
            business_id: selectedBusiness.id,
            customer_id: customer?.id || null,
            // Primary assigned resource for this field quote (first selected)
            resource_id: selectedResources[0]?.id || null,
            field_quote_address: fieldQuoteAddress || null,
            is_measurement: isMeasurement ?? false,
            start_date: startDate || null,
            start_time: startTime || null,
            end_date: endDate || null,
            end_time: endTime || null,
            color: fieldQuoteColor || null,
            secondary_color: fieldQuoteSecondaryColor || null,
            pipeline_opportunities_id: opportunityId
          })
          .select()
          .single();

        if (error) throw error;

        // Linking a field quote to a CRM opportunity is an action — record it so
        // the opportunity is marked read.
        if (opportunityId && customer?.id && fieldQuote?.id) {
          recordOpportunityActivity({
            businessId: selectedBusiness.id,
            customerId: customer.id,
            activityType: 'field_quote_linked',
            activityData: { field_quote_id: fieldQuote.id },
            specificOpportunityId: opportunityId,
          }).catch(err => console.error('Error recording field_quote_linked activity:', err));
        }

        if (initialProposal?.id && fieldQuote?.id) {
          try {
            const { error: proposalUpdateError } = await supabase
              .from('proposals')
              .update({ status: 'booked', field_quote_id: fieldQuote.id })
              .eq('id', initialProposal.id);

            if (proposalUpdateError) {
              console.error('Failed to link proposal to field quote:', proposalUpdateError);
              toast.error('Field quote created but proposal could not be linked');
            } else {
              const { data: proposalPayments, error: paymentsError } = await supabase
                .from('payments')
                .select('id')
                .eq('proposal_id', initialProposal.id);

              if (!paymentsError && proposalPayments?.length) {
                for (const payment of proposalPayments) {
                  const { error: payErr } = await supabase
                    .from('payments')
                    .update({ field_quote_id: fieldQuote.id })
                    .eq('id', payment.id);
                  if (payErr) {
                    console.error('Error linking payment to field quote:', payErr);
                  }
                }
              }
            }
          } catch (proposalLinkErr) {
            console.error('Error linking proposal to field quote:', proposalLinkErr);
          }
        }

        // Add resources if any are selected
        if (selectedResources.length > 0 && fieldQuote?.id) {
          const { error: resourcesError } = await supabase
            .from('project_field_quote_resources')
            .insert(
              selectedResources.map(resource => ({
                project_field_quote_id: fieldQuote.id,
                resource_id: resource.id,
                role: 'technician'
              }))
            );

          if (resourcesError) {
            console.error('Error adding resources:', resourcesError);
            // Don't fail the whole operation if resources fail
            toast.error('Field quote created but failed to assign some resources');
          }
        }

        if (selectedBusiness?.id && fieldQuote?.id) {
          triggerGcalSync(fieldQuote.id, 'create', selectedBusiness.id, 'field_quote');
        }

        // Reverse proximity check (same as booking creation): if an assigned
        // resource is already near the field quote address, auto-start their
        // job timer immediately. Best-effort — respects the auto GPS settings.
        if (fieldQuote?.id && fieldQuoteAddress && selectedResources.length > 0) {
          supabase.functions.invoke('check-booking-proximity', {
            body: { field_quote_id: fieldQuote.id, business_id: selectedBusiness.id }
          }).catch(err => {
            console.error('Error calling check-booking-proximity for field quote:', err);
          });
        }

        toast.success('Field quote scheduled successfully');
        onSuccess?.(fieldQuote?.id);
      }

      // Ensure the parent project status reflects that a field quote or measurement is scheduled
      try {
        if (projectId && selectedBusiness?.id) {
          const newStatus = isMeasurement ? 'Measurements scheduled' : 'Field Quote Scheduled';
          
          const { error: projectError } = await supabase
            .from('projects')
            .update({ status: newStatus })
            .eq('id', projectId)
            .eq('business_id', selectedBusiness.id);

          if (projectError) {
            console.error(`Error updating project status to ${newStatus}:`, projectError);
          }
        }
      } catch (statusError) {
        console.error('Unexpected error updating project status:', statusError);
      }
      if (mode === 'edit') {
        onSuccess?.();
      }
      onClose();
    } catch (err) {
      console.error('Error scheduling field quote:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to schedule field quote');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen && !isAnimating) return null;

  return createPortal(
    <div 
      className={`fixed inset-0 transition-opacity duration-200 ${
        isOpen ? 'bg-black/50' : 'bg-black/0'
      }`}
      style={{ 
        zIndex: resolvedZIndex,
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        paddingTop: isMobile ? 'env(safe-area-inset-top, 0px)' : '0',
        paddingBottom: isMobile ? 'env(safe-area-inset-bottom, 0px)' : '0'
      }}
      onClick={onClose}
      onTransitionEnd={handleTransitionEnd}
    >
      <div 
        className={`bg-white transform transition-all duration-200 ${
          isMobile 
            ? `relative w-full h-full ${isOpen ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'}`
            : `fixed inset-y-0 right-0 w-full md:w-[600px] shadow-2xl ${
                isOpen ? 'translate-x-0' : 'translate-x-full'
              }`
        }`}
        style={{ 
          fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
          zIndex: resolvedZIndex + 10
        }}
        onClick={e => e.stopPropagation()}
      >
        <div className="h-full flex flex-col overflow-hidden">
          {/* Close Button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 h-8 w-8 flex items-center justify-center text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 active:bg-gray-200 active:scale-95 transition-all duration-150 touch-manipulation z-10"
            style={{ WebkitTapHighlightColor: 'transparent' }}
          >
            <X className="h-5 w-5" />
          </button>

          {/* Header */}
          <div className="bg-gray-50 border-b border-gray-200 px-6 py-6 text-center relative">
            <h2
              className="text-2xl font-bold text-gray-900"
              style={{ letterSpacing: '-0.02em' }}
            >
              {isMeasurement ? 'Schedule Measurements' : 'Schedule Field Quote'}
            </h2>
          </div>

          {/* Content */}
          <form 
            onSubmit={(e) => {
              e.preventDefault();
              handleSave();
            }}
            className="flex-1 overflow-y-auto bg-white"
            style={{ 
              WebkitOverflowScrolling: 'touch',
              paddingBottom: isMobile ? 'env(safe-area-inset-bottom, 0px)' : '0'
            }}
          >
            <div className={`${isMobile ? 'p-4' : 'p-6'} space-y-6`} style={{
              paddingBottom: isMobile ? 'calc(1rem + env(safe-area-inset-bottom, 0px))' : '1rem'
            }}>
              {/* Project Title */}
              {projectTitle && (
                <div className="bg-white rounded-[12px] border border-gray-200/60 p-4 shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Project
                  </label>
                  <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                    <p className="text-sm font-medium text-gray-900">{projectTitle}</p>
                  </div>
                </div>
              )}

              {/* Customer Display Section */}
              {customer ? (
                <button
                  type="button"
                  onClick={() => setIsEditCustomerModalOpen(true)}
                  className="w-full text-left bg-white rounded-[12px] border border-gray-200/60 p-2 shadow-[0_1px_2px_rgba(0,0,0,0.05)] active:scale-[0.98] transition-all duration-150 touch-manipulation"
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                >
                  {/* Top row: Customer name and avatar */}
                  <div className="flex items-center space-x-2.5 mb-2">
                    <div className="h-6 w-6 rounded-full bg-blue-500 flex items-center justify-center text-white font-semibold text-[10px] flex-shrink-0">
                      {getPersonInitials(customer.first_name, customer.last_name)}
                    </div>
                    <div className="text-[14px] font-semibold text-gray-900 truncate leading-tight flex-1 min-w-0">
                      {customer.first_name} {customer.last_name}
                    </div>
                  </div>
                  
                  {/* Dividing line */}
                  <div className="border-t border-gray-200/60 my-2"></div>
                  
                  {/* Contact info */}
                  <div className="space-y-1.5 pt-1">
                    {customer.email && (
                      <div className="flex items-center space-x-2 text-sm text-gray-600">
                        <Mail className="h-3.5 w-3.5 text-gray-400" />
                        <span className="truncate">{customer.email}</span>
                      </div>
                    )}
                    {customer.phone && (
                      <div className="flex items-center space-x-2 text-sm text-gray-600">
                        <Phone className="h-3.5 w-3.5 text-gray-400" />
                        <span>{customer.phone}</span>
                      </div>
                    )}
                  </div>
                </button>
              ) : (
                <div className="bg-white rounded-[12px] border border-gray-200/60 p-4 shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setIsSelectCustomerModalOpen(true);
                    }}
                    className="w-full px-4 py-4 border-2 border-dashed border-blue-300 rounded-xl text-sm font-semibold text-blue-600 hover:text-blue-800 hover:border-blue-400 hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-150 active:scale-[0.98] touch-manipulation"
                    style={{ WebkitTapHighlightColor: 'transparent' }}
                  >
                    Select Customer
                  </button>
                </div>
              )}

              {/* Field Quote Address – only after a customer is selected */}
              {customer && (
                <div className="bg-white rounded-[12px] border border-gray-200/60 p-4 shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-semibold text-gray-700">
                      Field Quote Address
                    </label>
                    {fieldQuoteAddress && (customer as any)?.address && (
                      <button
                        type="button"
                        onClick={() => {
                          const customerAddress = (customer as any)?.address;
                          if (customerAddress) {
                            setFieldQuoteAddress(customerAddress);
                          }
                        }}
                        className="px-3 py-1.5 text-xs font-semibold text-blue-600 bg-blue-50 hover:bg-blue-100 active:bg-blue-200 rounded-xl transition-all duration-150 active:scale-[0.98] touch-manipulation"
                        style={{ WebkitTapHighlightColor: 'transparent' }}
                      >
                        Reset to Customer Address
                      </button>
                    )}
                  </div>
                  <AddressAutocompleteModal
                    value={fieldQuoteAddress}
                    onChange={(value) => {
                      setFieldQuoteAddress(value);
                    }}
                    onSelect={(address) => {
                      setFieldQuoteAddress(address.formatted);
                    }}
                    placeholder="Enter field quote service address"
                    className={`w-full px-4 py-3 text-base bg-white rounded-xl focus:outline-none focus:ring-2 transition-all duration-150 ${
                      fieldQuoteAddress
                        ? 'border border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                        : 'border-2 border-red-300 bg-red-50 focus:ring-red-500 focus:border-red-500'
                    }`}
                  />
                  {!fieldQuoteAddress && (
                    <p className="mt-2 text-xs text-red-600">
                      No address on file. Please enter a field quote address.
                    </p>
                  )}
                </div>
              )}

              {/* Services Display / Select Service – project-linked quotes only */}
              {!!projectId && customer && (
                projectServices.length > 0 ? (
                  <div className="bg-white rounded-[12px] border border-gray-200/60 overflow-hidden shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
                    <div className="p-4">
                      {/* Services & Products Section Header */}
                      <div className="mb-4 flex items-center justify-between border-t border-gray-200/60 pt-4">
                        <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">SERVICES & PRODUCTS</h3>
                        <div className="text-sm font-semibold text-gray-700 uppercase tracking-wide">TOTAL</div>
                      </div>

                      {/* Services List */}
                      <div className="space-y-4">
                        {projectServices.map((service) => (
                          <div key={service.id} className="border-b border-gray-100 pb-4 last:border-b-0">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="text-base font-medium text-gray-900">
                                  {service.name}
                                </div>
                              </div>
                              <div className="text-right ml-4">
                                <div className="text-lg font-semibold text-gray-900">
                                  {formatCurrency(service.total_amount)}
                                </div>
                                <div className="mt-1 text-sm text-gray-500">
                                  {service.duration_minutes >= 1440 
                                    ? `${Math.round(service.duration_minutes / 1440)} days`
                                    : `${Math.round(service.duration_minutes / 60)} hours`
                                  }
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="bg-white rounded-[12px] border border-gray-200/60 p-4 shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setIsSelectServiceModalOpen(true);
                      }}
                      className="w-full px-4 py-4 border-2 border-dashed border-blue-300 rounded-xl text-sm font-semibold text-blue-600 hover:text-blue-800 hover:border-blue-400 hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-150 active:scale-[0.98] touch-manipulation"
                      style={{ WebkitTapHighlightColor: 'transparent' }}
                    >
                      Select Service
                    </button>
                  </div>
                )
              )}

              {/* Resource Selection – after services (project) or address (standalone) */}
              {((projectId && projectServices.length > 0) || (isStandalone && !!customer && !!fieldQuoteAddress)) && (
                <div className="bg-white rounded-[12px] border border-gray-200/60 p-4 shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
                  <button
                    type="button"
                    onClick={() => setIsResourcesExpanded(!isResourcesExpanded)}
                    className="w-full flex items-center justify-end mb-4"
                  >
                    <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform duration-200 ${isResourcesExpanded ? 'rotate-180' : ''}`} />
                  </button>
                  
                  {/* Collapsed State - Show resource icons and add button */}
                  {!isResourcesExpanded && (
                    <div className="mb-2">
                      {selectedResources.length > 0 && (
                        <div className="flex items-center space-x-2 mb-2">
                          {selectedResources.map((resource) => (
                            <div key={resource.id} className="relative group">
                              <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center shadow-sm overflow-hidden">
                                {resource.avatar_url ? (
                                  <img 
                                    src={resource.avatar_url} 
                                    alt={`${resource.first_name} ${resource.last_name}`}
                                    className="w-full h-full object-cover"
                                  />
                                ) : (
                                  <span className="text-xs font-bold text-white">
                                    {getPersonInitials(resource.first_name, resource.last_name)}
                                  </span>
                                )}
                              </div>
                              {/* Tooltip */}
                              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10">
                                {resource.first_name} {resource.last_name}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      
                      {/* Add Resource Button in Collapsed State */}
                      <button
                        type="button"
                        onClick={() => setIsSelectResourceModalOpen(true)}
                        className="w-full px-2 py-1.5 border-2 border-dashed border-blue-300 rounded-lg text-xs font-medium text-blue-600 hover:text-blue-800 hover:border-blue-400 hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
                        style={{ minHeight: '32px' }}
                      >
                        {selectedResources.length === 0 ? 'Add Resources' : 'Add Another Resource'}
                      </button>
                    </div>
                  )}
                  
                  {/* Expanded State - Show full resource details */}
                  {isResourcesExpanded && (
                    <>
                      {selectedResources.length > 0 ? (
                        <div className="space-y-3">
                          {selectedResources.map((resource) => (
                            <div key={resource.id} className="flex items-center justify-between bg-gray-50 rounded-lg p-3 border border-gray-200">
                              <div className="flex items-center space-x-3">
                                <div className="h-10 w-10 rounded-full bg-blue-500 flex items-center justify-center overflow-hidden">
                                  {resource.avatar_url ? (
                                    <img 
                                      src={resource.avatar_url} 
                                      alt={`${resource.first_name} ${resource.last_name}`}
                                      className="w-full h-full object-cover"
                                    />
                                  ) : (
                                    <span className="text-sm font-medium text-white">
                                      {getPersonInitials(resource.first_name, resource.last_name)}
                                    </span>
                                  )}
                                </div>
                                <div>
                                  <div className="text-sm font-semibold text-gray-900">
                                    {resource.first_name} {resource.last_name}
                                  </div>
                                  {resource.email && (
                                    <div className="text-sm text-gray-600">
                                      {resource.email}
                                    </div>
                                  )}
                                </div>
                              </div>
                              <button
                                type="button"
                                onClick={() => handleResourceRemove(resource.id)}
                                className="text-sm font-medium text-red-600 hover:text-red-800 px-2 py-1 rounded-lg hover:bg-red-50 transition-colors"
                              >
                                Remove
                              </button>
                            </div>
                          ))}
                          <button
                            type="button"
                            onClick={() => setIsSelectResourceModalOpen(true)}
                            className="w-full mt-2 px-3 py-3 border-2 border-dashed border-blue-300 rounded-lg text-sm font-medium text-blue-600 hover:text-blue-800 hover:border-blue-400 hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
                          >
                            Add Another Resource
                          </button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() => setIsSelectResourceModalOpen(true)}
                          className="w-full px-3 py-3 border-2 border-dashed border-blue-300 rounded-lg text-sm font-medium text-blue-600 hover:text-blue-800 hover:border-blue-400 hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
                          style={{ minHeight: '48px' }}
                        >
                          Add Resources
                        </button>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* Scheduling — same SchedulePickerPanel as Create / Mobile booking */}
              {selectedResources.length > 0 && (
                <div
                  className="bg-white rounded-[12px] border border-gray-200/60 p-4 shadow-[0_1px_2px_rgba(0,0,0,0.05)]"
                  onClick={e => e.stopPropagation()}
                >
                  <div className="space-y-3">
                    <button
                      type="button"
                      onClick={() => setSchedulePickerOpen(true)}
                      disabled={loading}
                      className="w-full flex items-center gap-3 px-4 py-3.5 rounded-xl border border-gray-200 hover:border-blue-300 hover:bg-blue-50/30 transition-all text-left active:scale-[0.98] touch-manipulation disabled:opacity-50 disabled:cursor-not-allowed"
                      style={{ WebkitTapHighlightColor: 'transparent' }}
                    >
                      <div className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 text-blue-600 flex-shrink-0">
                        <Clock className="h-4 w-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">Schedule</div>
                        <div
                          className={`text-sm font-medium truncate ${
                            startDate && startTime ? 'text-gray-900' : 'text-blue-600'
                          }`}
                        >
                          {startDate && startTime ? (
                            <>
                              {(() => {
                                try {
                                  return `${format(new Date(`${startDate}T00:00:00`), 'EEE, MMM d')} ${format(
                                    new Date(`2000-01-01T${startTime}`),
                                    'h:mm a'
                                  )}`;
                                } catch {
                                  return startDate;
                                }
                              })()}
                              {endDate && endTime && (
                                <span className="text-gray-500">
                                  {' → '}
                                  {(() => {
                                    try {
                                      return `${format(new Date(`${endDate}T00:00:00`), 'MMM d')} ${format(
                                        new Date(`2000-01-01T${endTime}`),
                                        'h:mm a'
                                      )}`;
                                    } catch {
                                      return endDate;
                                    }
                                  })()}
                                </span>
                              )}
                            </>
                          ) : (
                            'Set schedule'
                          )}
                        </div>
                      </div>
                      <ChevronDown className="h-4 w-4 text-gray-400 flex-shrink-0 -rotate-90" />
                    </button>

                    {schedulePickerOpen && selectedBusiness?.id && (
                      <SchedulePickerPanel
                        isOpen={schedulePickerOpen}
                        onClose={() => setSchedulePickerOpen(false)}
                        businessId={selectedBusiness.id}
                        showDropOff={false}
                        defaultView="month"
                        startDate={startDate}
                        startTime={startTime}
                        endDate={endDate}
                        endTime={endTime}
                        serviceDuration={scheduleServiceMinutes}
                        onConfirm={(sDate, sTime, eDate, eTime) => {
                          setStartDate(sDate);
                          setStartTime(sTime);
                          setEndDate(eDate);
                          setEndTime(eTime);
                          setIsResourcesExpanded(false);
                        }}
                        disabled={loading}
                      />
                    )}
                  </div>
                </div>
              )}

              {/* Colour Picker — only after schedule is set (mirrors Create Booking modal) */}
              {startDate && startTime && (
                <div className="bg-white rounded-[12px] border border-gray-200/60 p-4 shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
                  <h3 className="text-sm font-semibold text-gray-900 mb-3">
                    {isMeasurement ? 'Measurement Colour' : 'Field Quote Colour'}
                  </h3>
                  <div className="grid grid-cols-5 gap-2">
                    {/* Current Color */}
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedColorMethod('custom');
                        setIsColorPickerModalOpen(true);
                      }}
                      className={`flex flex-col items-center justify-center p-2 border-2 rounded-lg transition-colors ${
                        selectedColorMethod === 'custom'
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300 opacity-60 hover:opacity-80'
                      }`}
                    >
                      <div className="w-8 h-8 rounded-full mb-1 overflow-hidden">
                        {fieldQuoteSecondaryColor ? (
                          <div className="w-full h-full flex">
                            <div className="w-1/2 h-full" style={{ backgroundColor: fieldQuoteColor }} />
                            <div className="w-1/2 h-full" style={{ backgroundColor: fieldQuoteSecondaryColor }} />
                          </div>
                        ) : (
                          <div className="w-full h-full" style={{ backgroundColor: fieldQuoteColor }} />
                        )}
                      </div>
                      <span className="text-xs font-medium text-gray-900">Current</span>
                    </button>

                    {/* Color Presets */}
                    {colorPresets.map((preset) => (
                      <div key={preset.id} className="relative">
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedColorMethod(preset.id);
                            setFieldQuoteColor(preset.primary_color);
                            setFieldQuoteSecondaryColor(preset.secondary_color || null);
                          }}
                          className={`flex flex-col items-center justify-center p-2 border-2 rounded-lg transition-colors w-full ${
                            selectedColorMethod === preset.id
                              ? 'border-blue-500 bg-blue-50'
                              : 'border-gray-200 hover:border-gray-300 opacity-60 hover:opacity-80'
                          }`}
                        >
                          <div className="w-8 h-8 rounded-full mb-1 overflow-hidden">
                            {preset.secondary_color ? (
                              <div className="w-full h-full flex">
                                <div className="w-1/2 h-full" style={{ backgroundColor: preset.primary_color }} />
                                <div className="w-1/2 h-full" style={{ backgroundColor: preset.secondary_color }} />
                              </div>
                            ) : (
                              <div className="w-full h-full" style={{ backgroundColor: preset.primary_color }} />
                            )}
                          </div>
                          <span className="text-xs font-medium text-gray-900">{preset.title}</span>
                        </button>
                        <button
                          type="button"
                          onClick={async (e) => {
                            e.stopPropagation();
                            const confirmed = window.confirm(
                              `Are you sure you want to delete the "${preset.title}" color preset? This action cannot be undone.`
                            );
                            if (!confirmed) return;
                            try {
                              await deleteColorPreset(preset.id);
                              setColorPresets((prev) => prev.filter((p) => p.id !== preset.id));
                              if (selectedColorMethod === preset.id) {
                                setSelectedColorMethod('custom');
                              }
                            } catch (error) {
                              console.error('Error deleting color preset:', error);
                            }
                          }}
                          className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center text-xs font-medium shadow-sm transition-colors"
                        >
                          ×
                        </button>
                      </div>
                    ))}

                    {/* Add Preset Button */}
                    <button
                      type="button"
                      onClick={() => setIsCreateColorPresetModalOpen(true)}
                      className="flex flex-col items-center justify-center p-2 border-2 border-dashed border-gray-300 rounded-lg hover:border-gray-400 hover:bg-gray-50 transition-colors opacity-60 hover:opacity-80"
                    >
                      <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center mb-1">
                        <Plus className="w-4 h-4 text-gray-500" />
                      </div>
                      <span className="text-xs font-medium text-gray-600">Add</span>
                    </button>
                  </div>
                </div>
              )}

              {/* Submit Button */}
              <div className="bg-white p-6">
                <button
                  type="submit"
                  disabled={loading || !isFormComplete}
                  className={`w-full inline-flex items-center justify-center px-6 py-3.5 text-base font-semibold text-white rounded-xl transition-all duration-150 active:scale-[0.98] touch-manipulation shadow-sm disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none ${
                    !loading && isFormComplete
                      ? 'bg-blue-600 hover:bg-blue-700 active:bg-blue-800'
                      : 'bg-gray-300 cursor-not-allowed'
                  }`}
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                >
                  {loading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2"></div>
                      Scheduling...
                    </>
                  ) : isMeasurement ? (
                    'Schedule Measurements'
                  ) : (
                    'Schedule Field Quote'
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>

      {/* Modals */}
      <SelectCustomerModal
        isOpen={isSelectCustomerModalOpen}
        onClose={() => setIsSelectCustomerModalOpen(false)}
        onSelect={handleCustomerSelect}
      />

      <SelectServiceModal
        isOpen={isSelectServiceModalOpen}
        onClose={() => setIsSelectServiceModalOpen(false)}
        onAdd={handleServiceAdd}
      />

      <SelectResourceModal
        isOpen={isSelectResourceModalOpen}
        onClose={() => setIsSelectResourceModalOpen(false)}
        onConfirm={({ resources }) => {
          setSelectedResources(resources);
        }}
        selectedResources={selectedResources}
        confirmLabel="Confirm"
      />

      <EditCustomerModal
        isOpen={isEditCustomerModalOpen}
        onClose={() => setIsEditCustomerModalOpen(false)}
        onSuccess={(updatedCustomer) => {
          setCustomer(updatedCustomer);
          // If customer has an address and field quote address is empty, initialize from updated address
          if (!fieldQuoteAddress && (updatedCustomer as any)?.address) {
            setFieldQuoteAddress((updatedCustomer as any).address || '');
          }
        }}
        customer={customer}
      />

      <ColorPickerModal
        isOpen={isColorPickerModalOpen}
        onClose={() => setIsColorPickerModalOpen(false)}
        onColorSelect={(color, secondaryColor) => {
          setSelectedColorMethod('custom');
          setFieldQuoteColor(color);
          setFieldQuoteSecondaryColor(secondaryColor || null);
          setIsColorPickerModalOpen(false);
        }}
        currentColor={fieldQuoteColor}
        currentSecondaryColor={fieldQuoteSecondaryColor || undefined}
      />

      {/* Portaled to body inside an elevated wrapper so it stacks above this
          field quote modal (z 100150/100160) without changing the shared
          CreateColorPresetModal's own z-index. */}
      {isCreateColorPresetModalOpen &&
        createPortal(
          <div style={{ position: 'relative', zIndex: resolvedZIndex + 50 }}>
            <CreateColorPresetModal
              isOpen={isCreateColorPresetModalOpen}
              onClose={() => setIsCreateColorPresetModalOpen(false)}
              onSave={async (title, primaryColor, secondaryColor) => {
                if (!selectedBusiness?.id) return;
                try {
                  const newPreset = await createColorPreset({
                    business_id: selectedBusiness.id,
                    title,
                    primary_color: primaryColor,
                    secondary_color: secondaryColor
                  });
                  setColorPresets((prev) => [...prev, newPreset]);
                  setIsCreateColorPresetModalOpen(false);
                } catch (error) {
                  console.error('Error creating color preset:', error);
                }
              }}
            />
          </div>,
          document.body
        )}
    </div>,
    document.body
  );
}

